var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Observable } from "@microsoft/fast-element";
import { attr, FASTElement, observable, Updates } from "@microsoft/fast-element";
import { isHTMLElement } from "@microsoft/fast-web-utilities";
import { AccordionMenuPanel } from "../accordion-menu-panel/accordion-menu-panel.js";
import { AccordionMenuExpandModes } from "./accordion-menu.options.js";
import { MenuItem } from "../menu-item/menu-item.js";
import { MenuItemRoles } from "../menu-item/menu-item.options.js";
import { MenuList } from "../menu-list/menu-list.js";
/**
 * AccordionMenu
 * @summary An AccordionMenu component that provides an interactive accordion interface, allowing for expandable and collapsible sections. Implements ARIA Accordion.
 *
 * @example
 * ```html
 * <fabric-accordion-menu expand-mode="single">
 *   <fabric-accordion-item>
 *     <fabric-accordion-menu-panel>
 *       <!-- content for first panel -->
 *     </fabric-accordion-menu-panel>
 *   </fabric-accordion-item>
 *   <fabric-accordion-item>
 *     <fabric-accordion-menu-panel>
 *       <!-- content for second panel -->
 *     </fabric-accordion-menu-panel>
 *   </fabric-accordion-item>
 *   <!-- additional accordion items here -->
 * </fabric-accordion-menu>
 * ```
 *
 * @attr {AccordionMenuExpandMode} expand-mode - Controls the expand mode of the Accordion, either allowing single or multiple item expansion.
 *
 * @prop {ElementInternals} elementInternals - Internal element state used to manage the component.
 * @prop {HTMLElement[]} slottedElements - The slotted accordion menu items.
 * @prop {AccordionMenuExpandMode} expandmode - Controls the expand mode of the Accordion, either allowing single or multiple item expansion.
 *
 * @slot - The default slot for the accordion items.
 *
 * @csspart accordion-menu - Represents the accordion menu container.
 *
 * @method expandmodeChanged - Handles changes to the `expandmode` attribute.
 * @method handleChange - Handles changes to observed properties.
 * @method clearSelections - Clears the selections of menu items.
 * @method findExpandedItem - Finds the first expanded item in the accordion.
 * @method setItems - Sets up the accordion items.
 * @method isSingleExpandMode - Checks if the accordion is in single expand mode.
 * @method setSingleExpandMode - Controls the behavior of the accordion in single expand mode.
 *
 * @fires change - Fires a custom 'change' event when the active item changes.
 *
 * @extends FASTElement
 * @tagname fabric-accordion-menu
 * @public
 */
export class AccordionMenu extends FASTElement {
    /** Constructor for the AccordionMenu component. */
    constructor() {
        super();
        /** Internal element state used to manage the component. */
        this.elementInternals = this.attachInternals();
        /**
         * Controls the expand mode of the Accordion, either allowing
         * single or multiple item expansion.
         * HTML attribute: expand-mode
         * @public
         */
        this.expandmode = AccordionMenuExpandModes.multi;
        /**
         * The slotted accordion menu items.
         * @public
         */
        this.slottedElements = [];
        /**
         * check if the item is a menu item
         */
        this.isMenuItemElement = (el) => {
            return (el instanceof MenuItem ||
                (isHTMLElement(el) &&
                    el.getAttribute("role") in MenuList.focusableElementRoles));
        };
        /**
         * Removes event listeners from items.
         */
        this.removeItemListeners = (items) => {
            items.forEach((item) => {
                Observable.getNotifier(item).unsubscribe(this, "disabled");
                Observable.getNotifier(item).unsubscribe(this, "expanded");
            });
        };
        this.elementInternals.role = "menu";
    }
    /** Lifecycle method called when the component is connected to the DOM. */
    connectedCallback() {
        super.connectedCallback();
        Updates.enqueue(() => {
            this.setItems();
        });
    }
    /** Lifecycle method called when the component is disconnected from the DOM. */
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeItemListeners(this.slottedElements);
        this.slottedElements = [];
    }
    /**
     * Handles changes to the `expandmode` attribute.
     * @param prev - The previous value.
     * @param next - The next value.
     */
    expandmodeChanged(prev, next) {
        if (!this.$fastController.isConnected) {
            return;
        }
        const expandedItem = this.findExpandedItem();
        if (!expandedItem) {
            return;
        }
        if (next === AccordionMenuExpandModes.single) {
            this.setSingleExpandMode(expandedItem);
        }
        else {
            expandedItem === null || expandedItem === void 0 ? void 0 : expandedItem.removeAttribute("aria-disabled");
        }
    }
    /**
     * Handles changes to observed properties.
     * @param source - The source element.
     * @param propertyName - The name of the property that changed.
     */
    handleChange(source, propertyName) {
        if (propertyName === "disabled") {
            this.setItems();
        }
        else if (propertyName === "expanded") {
            const sourcePanel = source;
            if (sourcePanel.expanded && this.isSingleExpandMode()) {
                this.setSingleExpandMode(sourcePanel);
            }
        }
    }
    /**
     * Clears the selections of menu items.
     */
    clearSelections() {
        if (this.slottedElements === undefined) {
            return;
        }
        this.slottedElements.forEach((item) => {
            if (item instanceof AccordionMenuPanel && item.menuList) {
                item.menuList.forEach((menuItem) => {
                    var _a;
                    const menuList = menuItem;
                    (_a = menuList.menuItems) === null || _a === void 0 ? void 0 : _a.forEach((menuItem) => {
                        if (menuItem instanceof MenuItem) {
                            menuItem.checked = false;
                        }
                    });
                });
            }
        });
    }
    /**
     * Finds the first expanded item in the accordion.
     */
    findExpandedItem() {
        var _a;
        if (this.slottedElements.length === 0) {
            return null;
        }
        return ((_a = this.slottedElements.find((item) => item instanceof AccordionMenuPanel && item.expanded)) !== null && _a !== void 0 ? _a : this.slottedElements[0]);
    }
    /**
     * Sets up the accordion items.
     */
    setItems() {
        var _a;
        if (this.slottedElements.length === 0) {
            return;
        }
        const children = Array.from(this.children);
        this.removeItemListeners(children);
        children.forEach((child) => Observable.getNotifier(child).subscribe(this, "disabled"));
        this.slottedElements = children.filter((child) => !child.hasAttribute("disabled"));
        // Set initial tabindex for each accordion item
        this.slottedElements.forEach((item) => {
            if (item instanceof AccordionMenuPanel) {
                Observable.getNotifier(item).subscribe(this, "expanded");
            }
        });
        if (this.isSingleExpandMode()) {
            const expandedItem = this.findExpandedItem();
            this.setSingleExpandMode(expandedItem);
        }
        /**
         * Set the indent attribute on MenuItem elements based on their
         * position in the MenuList. Each MenuItem element has a data-indent attribute that is
         * used to set the indent of the element's start slot content.
         */
        const filteredMenuListItems = (_a = this.slottedElements) === null || _a === void 0 ? void 0 : _a.filter(this.isMenuItemElement);
        const indent = filteredMenuListItems === null || filteredMenuListItems === void 0 ? void 0 : filteredMenuListItems.reduce((accum, current) => {
            const elementValue = AccordionMenu.elementIndent(current);
            return Math.max(accum, elementValue);
        }, 0);
        filteredMenuListItems === null || filteredMenuListItems === void 0 ? void 0 : filteredMenuListItems.forEach((item) => {
            if (item instanceof MenuItem) {
                item.setAttribute("data-indent", `${indent}`);
            }
        });
    }
    static elementIndent(el) {
        const role = el.getAttribute("role");
        const startSlot = el.querySelector("[slot=start]");
        if (role && role !== MenuItemRoles.menuitem) {
            return startSlot ? 2 : 1;
        }
        return startSlot ? 1 : 0;
    }
    /**
     * Checks if the accordion is in single expand mode.
     */
    isSingleExpandMode() {
        return this.expandmode === AccordionMenuExpandModes.single;
    }
    /**
     * Controls the behavior of the accordion in single expand mode.
     */
    setSingleExpandMode(expandedItem) {
        if (this.slottedElements.length === 0) {
            return;
        }
        const currentItems = Array.from(this.slottedElements);
        currentItems.forEach((item) => {
            if (item instanceof AccordionMenuPanel) {
                if (item === expandedItem) {
                    item.expanded = true;
                    this.setAttribute("aria-disabled", "true");
                }
                else {
                    item.expanded = false;
                    if (!item.hasAttribute("disabled")) {
                        this.removeAttribute("aria-disabled");
                    }
                }
            }
        });
    }
}
__decorate([
    attr({ attribute: "expand-mode" })
], AccordionMenu.prototype, "expandmode", void 0);
__decorate([
    observable
], AccordionMenu.prototype, "slottedElements", void 0);
//# sourceMappingURL=accordion-menu.js.map